/**
 * Shared TanStack Query client + centralized query keys.
 *
 * Server data lives here (TanStack Query); Zustand is reserved for UI/session
 * state only. Keys are centralized so mutations can invalidate consistently.
 */

import { QueryCache, QueryClient } from "@tanstack/react-query";
import { isApiException, isUnauthorized } from "./api-errors";
import { clearPersistedQueries } from "./query-persist";
import { clearTokens } from "./auth-token";
import { useAuthStore } from "@/features/auth/auth.store";
import { isSessionValidating } from "@/features/auth/restore-flag";
import { toast } from "@/stores/toast.store";

/**
 * Global handler for an authenticated request that comes back 401 mid-session.
 *
 * By the time a 401 reaches here it is UNRECOVERABLE: `api-client.ts` already
 * tried to refresh the access token and replay the request once. A recoverable
 * 401 (expired access token, valid refresh token) is refreshed + retried and
 * never surfaces as an error, so the user stays logged in. Only a missing/
 * invalid/expired refresh token — or a refresh rejected with 401 — gets here.
 *
 * The session is ended exactly once (the `status !== "authenticated"` guard
 * dedups the burst of failing queries into a single logout + single toast).
 * 403 (insufficient permission) is left alone — it is not an authentication
 * failure. NetworkError never reaches here (it is not an ApiException), so a
 * backend outage — including one during refresh — does not log the user out.
 *
 * During the startup optimistic-restore validation window the session-restore
 * hook is the sole authority on the terminal-401 outcome (it ends the session
 * SILENTLY, matching a cold reload with a dead token). We defer here so a data
 * query racing the background `/auth/me` cannot toast "session expirée" on open
 * or log out twice. See `restore-flag.ts`.
 */
function handleSessionExpiry(error: unknown): void {
  if (!isApiException(error) || error.status !== 401) return;
  if (isSessionValidating()) return;
  if (useAuthStore.getState().status !== "authenticated") return;
  clearTokens();
  useAuthStore.getState().setAnonymous();
  clearPrivateCaches(queryClient);
  toast.error("Votre session a expiré. Veuillez vous reconnecter.");
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => handleSessionExpiry(error),
  }),
  defaultOptions: {
    queries: {
      // Instant-navigation + COLLABORATION cache policy. Pages always paint
      // instantly from cache (never skeletons for cached data); freshness
      // comes from silent background revalidation:
      //  - staleTime 60s: navigating to a page (or refocusing the window)
      //    revalidates anything older than a minute — so a teammate's change
      //    appears without logging out (5 min felt like "the app is broken").
      //  - refetchOnWindowFocus: switching back to the app re-checks stale
      //    data — the standard collaborative-app behavior.
      //  - gcTime 60 min: an unvisited page's cache survives long detours, so
      //    coming back paints instantly + revalidates instead of skeletons.
      staleTime: 60_000,
      gcTime: 60 * 60_000,
      refetchOnWindowFocus: true,
      // Never retry auth/permission failures; retry transient errors once.
      retry: (failureCount, error) => (isUnauthorized(error) ? false : failureCount < 1),
    },
  },
});

export const queryKeys = {
  currentUser: ["auth", "me"] as const,
  clientsList: (params: unknown) => ["clients", "list", params] as const,
  client: (id: string) => ["clients", "detail", id] as const,
  dashboard: (period: string) => ["reports", "dashboard", period] as const,
  dashboardSeries: (months: number) => ["reports", "monthly-series", months] as const,
  recentActivity: ["reports", "recent-activity"] as const,
  paymentsList: (params: unknown) => ["payments", "list", params] as const,
  payment: (id: string) => ["payments", "detail", id] as const,
  walletsList: (params: unknown) => ["wallets", "list", params] as const,
  walletAlerts: (params: unknown) => ["wallets", "alerts", params] as const,
  walletTransactions: (params: unknown) => ["wallet-transactions", "list", params] as const,
  adSpendList: (params: unknown) => ["ad-spend", "list", params] as const,
  expensesList: (params: unknown) => ["expenses", "list", params] as const,
  servicesList: (params: unknown) => ["services", "list", params] as const,
  service: (id: string) => ["services", "detail", id] as const,
  treasuryAccounts: ["treasury", "accounts"] as const,
  treasuryMovements: (params: unknown) => ["treasury", "movements", params] as const,
  treasuryReconciliations: ["treasury", "reconciliations"] as const,
  // Paramétrée par compte : la fiche d'un compte montre SON historique, la page
  // montre le global — deux entrées de cache distinctes, une seule invalidation.
  treasuryAdjustments: (accountId?: string) =>
    ["treasury", "adjustments", accountId ?? "all"] as const,
  leadsList: (params: unknown) => ["leads", "list", params] as const,
  campaignsList: (params: Record<string, unknown>) => ["campaigns", "list", params] as const,
  campaignReport: (id: string) => ["campaigns", "report", id] as const,
  projectsList: (params: unknown) => ["projects", "list", params] as const,
  quotesList: (params: unknown) => ["invoices", "quotes", params] as const,
  invoicesList: (params: unknown) => ["invoices", "list", params] as const,
  tasksList: (params: unknown) => ["tasks", "list", params] as const,
  myTasks: ["tasks", "my"] as const,
  task: (id: string) => ["tasks", "detail", id] as const,
  subscriptionsList: (params: unknown) => ["subscriptions", "list", params] as const,
  teamList: (params: unknown) => ["team", "list", params] as const,
  teamDetail: (id: string) => ["team", "detail", id] as const,
  teamMe: ["team", "me"] as const,
  settingsList: ["settings", "list"] as const,
  notificationsList: (params: unknown) => ["notifications", "list", params] as const,
  // Vue d'ensemble compta — clé centralisée pour que les mutations financières
  // (factures, dettes, trésorerie, abonnements…) puissent l'invalider (ADR-001).
  accountingOverview: ["accounting", "overview"] as const,
};

/** Prefixes used to invalidate a feature's queries (list + detail) after a mutation. */
export const REPORTS_QUERY_PREFIX = ["reports"] as const;
export const CLIENTS_QUERY_PREFIX = ["clients"] as const;
export const PAYMENTS_QUERY_PREFIX = ["payments"] as const;
export const WALLETS_QUERY_PREFIX = ["wallets"] as const;
export const AD_SPEND_QUERY_PREFIX = ["ad-spend"] as const;
export const EXPENSES_QUERY_PREFIX = ["expenses"] as const;
export const SERVICES_QUERY_PREFIX = ["services"] as const;
export const TREASURY_QUERY_PREFIX = ["treasury"] as const;
export const INVOICES_QUERY_PREFIX = ["invoices"] as const;
export const LEADS_QUERY_PREFIX = ["leads"] as const;
export const PROJECTS_QUERY_PREFIX = ["projects"] as const;
export const CAMPAIGNS_QUERY_PREFIX = ["campaigns"] as const;
export const TASKS_QUERY_PREFIX = ["tasks"] as const;
export const SUBSCRIPTIONS_QUERY_PREFIX = ["subscriptions"] as const;
export const TEAM_QUERY_PREFIX = ["team"] as const;
export const SETTINGS_QUERY_PREFIX = ["settings"] as const;
export const NOTIFICATIONS_QUERY_PREFIX = ["notifications"] as const;
export const ACCOUNTING_QUERY_PREFIX = ["accounting"] as const;

/**
 * Wipe the cache when a session ends — logout OR a terminal 401 — so one user's
 * data can never flash for the next. This matters because a same-tab re-login is
 * a pure client-side state transition (no page reload), so nothing ELSE resets
 * the cache between two users.
 *
 * We clear EVERYTHING rather than maintain an allow-list of "private" prefixes:
 * virtually all cached data is per-user / per-tenant, new query families are
 * added constantly (accounting, ai-agents, partners, marketing, platform, …),
 * and a hand-curated list silently drifts out of date — leaking exactly the
 * newest features' data. The public surfaces (client portal, shared report) use
 * their OWN auth and simply refetch on demand, so clearing them here is
 * harmless. Shared by `useLogout` and both terminal-401 paths (global expiry
 * handler + startup session restore) so ending a session is consistent.
 */
export function clearPrivateCaches(client: QueryClient): void {
  client.clear();
  // The on-disk snapshot mirrors the in-memory cache — wipe it too, or the
  // next boot would hydrate the previous user's data back in.
  clearPersistedQueries();
}
